import { CustomerAppCoupon } from './customerAppCoupon';
import { CustomerAppPaymentMethods } from './customerAppPaymentMethods';
import { CustomerAppPoints } from './customerAppPoints';
import { z } from 'zod';

export type CustomerAppDescription = z.infer<typeof CustomerAppDescription>;

export const CustomerAppDescription = z.object({
    /** An identifier for the customer app implementation that is used to identify the customer app. */
    customerAppId: z.string().nullish(),
    /** A friendly name that is used to describe the customer app. */
    customerAppName: z.string().nullish(),
    /** Metadata information that was encoded in the scanned code which is the entry point for authenticating against the customer app on some users' behalf. */
    scannedCodeMetadata: z.record(z.string(), z.string().nullish()),
    /** Metadata that was returned by the authentication method(s) against the customer app backend. */
    loginMetadata: z.record(z.string(), z.string().nullish()),
    /** A list of available coupons and vouchers for the customer that the customer has avaiable via the customer app or other sources which are either directly or indirectly available. */
    coupons: z.array(CustomerAppCoupon).nullish(),
    /** A list of available points for the customer that the customer has avaiable via the customer app or other sources which are either directly or indirectly available. */
    points: z.array(CustomerAppPoints).nullish(),
    /** A list of available payment features per source. All sources have to specify whether they are enabled or not. If a source is not enabled, it is not available for the customer. */
    paymentMethods: z.array(CustomerAppPaymentMethods).nullish(),
    /** A barcode that identifies the customer app on a point of sale system. Typically this code is the same used to link a customer app at a cashier to a customer account. */
    posIdentificationGtin: z.string().nullish(),
    /** Implementation specific metadata that is used to describe the customer app implementation. */
    metadata: z.record(z.string(), z.string()).nullish(),

    linkedLoyaltyPrograms: z
        .record(
            z.string(),
            z.object({
                cardGtin: z.string().nullish(),
                cardNumber: z.string().nullish(),
                linkedDate: z.coerce.date().nullish(),
                points: z.number().nullish(),
            }),
        )
        .nullish(),
});
